import type { TemplateListItemDto } from '@/apis/backend/generated/models';

import { useState, forwardRef } from 'react';
import { VirtuosoGrid } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '@/context/organization-context';
import { useTemplatesControllerListTemplates } from '@/apis/backend/generated/templates/templates';

import { Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Chip,
  Stack,
  alpha,
  Button,
  Dialog,
  useTheme,
  TextField,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  InputAdornment,
  CircularProgress,
} from '@mui/material';

const MY_SOURCES = new Set([
  'user_created',
  'klaviyo_synced',
  'user_uploaded',
  'figma_import',
]);

interface ReferenceTemplatePickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (template: TemplateListItemDto) => void;
  /** Override the default (AI-reference) header copy when reused as a plain picker. */
  title?: string;
  subtitle?: string;
}

// Modal that lists the user's own previously generated emails — same dataset
// as /dashboard/templates/my. Used as the AI structural-reference picker and,
// with overridden copy, as a plain template picker (e.g. a flow email step).
// Click a card to select and close.
export function ReferenceTemplatePicker({
  open,
  onClose,
  onSelect,
  title,
  subtitle,
}: ReferenceTemplatePickerProps) {
  const { t } = useTranslation('emailGen');
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id ?? '';
  const clientId = activeClient?.id ?? '';
  const [search, setSearch] = useState('');
  // Virtuoso needs a real scroll element. Owning our own here means the
  // modal's DialogContent scroll never engages — every card is rendered
  // through the virtualized grid, regardless of how the dialog is sized.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

  const { data, isLoading } = useTemplatesControllerListTemplates(
    orgId,
    clientId,
    {
      search: search.trim() || undefined,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      limit: 60,
    },
    {
      query: {
        enabled: open && !!orgId && !!clientId,
      },
    },
  );

  const templates = (data?.templates ?? []).filter(
    (tpl) =>
      tpl.source != null &&
      MY_SOURCES.has(tpl.source) &&
      (tpl.isLatestVersion ?? true),
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title ?? t('referenceTemplatePicker.title', 'Pick a reference email')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle ??
              t(
                'referenceTemplatePicker.subtitle',
                'The AI will use this as visual inspo and can copy its layout/copy when you ask for changes like "use this but swap the hero".',
              )}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('referenceTemplatePicker.searchPlaceholder', 'Search your emails…')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : templates.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {search.trim()
                ? t('referenceTemplatePicker.empty.noResults', 'No emails match your search.')
                : t('referenceTemplatePicker.empty.noEmails', "You haven't generated any emails yet.")}
            </Typography>
          </Stack>
        ) : (
          <Box
            ref={setScrollEl}
            sx={{
              maxHeight: '60vh',
              overflowY: 'auto',
            }}
          >
            {scrollEl && (
              <VirtuosoGrid
                customScrollParent={scrollEl}
                totalCount={templates.length}
                overscan={400}
                components={gridComponents}
                computeItemKey={(index) => templates[index].id}
                itemContent={(index) => {
                  const tpl = templates[index];
                  return (
                    <ReferenceTemplateCard
                      template={tpl}
                      onClick={() => {
                        onSelect(tpl);
                        onClose();
                      }}
                    />
                  );
                }}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">
          {t('common:cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const GridList = forwardRef<
  HTMLDivElement,
  { style?: React.CSSProperties; children?: React.ReactNode }
>(({ style, children, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
      gap: 16,
      ...style,
    }}
  >
    {children}
  </div>
));
GridList.displayName = 'ReferenceTemplatePickerGridList';

const GridItem = forwardRef<
  HTMLDivElement,
  { style?: React.CSSProperties; children?: React.ReactNode }
>(({ style, children, ...props }, ref) => (
  <div ref={ref} {...props} style={{ display: 'flex', ...style }}>
    {children}
  </div>
));
GridItem.displayName = 'ReferenceTemplatePickerGridItem';

const gridComponents = {
  List: GridList,
  Item: GridItem,
};

function ReferenceTemplateCard({
  template,
  onClick,
}: {
  template: TemplateListItemDto;
  onClick: () => void;
}) {
  const { t } = useTranslation('emailGen');
  const theme = useTheme();
  // Cache-bust the thumbnail using lastCompiledAt so updates surface
  // immediately instead of being pinned to a stale CDN copy.
  const thumb = template.thumbnailUrl
    ? template.lastCompiledAt
      ? `${template.thumbnailUrl}${template.thumbnailUrl.includes('?') ? '&' : '?'}t=${encodeURIComponent(template.lastCompiledAt)}`
      : template.thumbnailUrl
    : null;

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        width: '100%',
        cursor: 'pointer',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        transition: 'border-color 120ms, box-shadow 120ms, transform 120ms',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
          transform: 'translateY(-1px)',
        },
        '&:focus-visible': {
          outline: 'none',
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.4)}`,
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '3 / 4',
          bgcolor: alpha(theme.palette.action.hover, 0.4),
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {template.renderMode === 'builder' && (
          // Canvas emails can be edited section-by-section by the canvas
          // agent ("this email but change the hero"); other references are
          // visual inspiration only. The badge tells those apart at a glance.
          <Chip
            size="small"
            label={t('referenceTemplatePicker.card.canvasBadge', 'Canvas')}
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              height: 20,
              fontSize: '0.6875rem',
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              border: `1px solid ${theme.palette.divider}`,
            }}
          />
        )}
        {thumb ? (
          <img
            src={thumb}
            alt={template.name}
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
            }}
            loading="lazy"
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ width: '100%', height: '100%' }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('referenceTemplatePicker.card.noPreview', 'No preview')}
            </Typography>
          </Stack>
        )}
      </Box>
      <Box sx={{ p: 1.25 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.3,
          }}
        >
          {template.name}
        </Typography>
      </Box>
    </Box>
  );
}
